import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import "./DropdownMenu.css";

interface DropdownMenuProps {
  id: string;
  label: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  placement?: "floating" | "inline";
}

interface DropdownMenuItemBaseProps {
  children: ReactNode;
  icon?: ReactNode;
  value?: ReactNode;
  variant?: "default" | "danger";
}

type DropdownMenuButtonItemProps = DropdownMenuItemBaseProps & {
  checked?: boolean;
  onClick: () => void | Promise<void>;
  to?: never;
  onNavigate?: never;
};

type DropdownMenuLinkItemProps = DropdownMenuItemBaseProps & {
  to: string;
  onNavigate?: () => void;
  onClick?: never;
};

type DropdownMenuItemProps =
  | DropdownMenuButtonItemProps
  | DropdownMenuLinkItemProps;

export function DropdownMenu({
  children,
  className = "",
  id,
  label,
  placement = "floating",
  style,
}: DropdownMenuProps) {
  const menuClassName = [
    "dropdown-menu",
    placement === "inline" ? "dropdown-menu--inline" : "",
    className,
  ].filter(Boolean).join(" ");

  return (
    <div className={menuClassName} id={id} role="menu" aria-label={label} style={style}>
      {children}
    </div>
  );
}

export function DropdownMenuItem(props: DropdownMenuItemProps) {
  const {
    children,
    icon,
    value,
    variant = "default",
  } = props;
  const checked = "checked" in props ? props.checked : undefined;
  const className = [
    "dropdown-menu__item",
    variant === "danger" ? "dropdown-menu__item--danger" : "",
    checked === true ? "dropdown-menu__item--checked" : "",
    // An item without an icon draws no icon cell, so its layout drops the
    // column that cell would have filled.
    icon ? "" : "dropdown-menu__item--no-icon",
  ].filter(Boolean).join(" ");
  const content = (
    <>
      {icon ? (
        <span className="dropdown-menu__item-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="dropdown-menu__item-label">{children}</span>
      {value ? (
        <span className="dropdown-menu__item-value">{value}</span>
      ) : null}
    </>
  );

  if ("to" in props && props.to) {
    return (
      <Link
        to={props.to}
        className={className}
        role="menuitem"
        onClick={props.onNavigate}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      role={typeof checked === "boolean" ? "menuitemcheckbox" : "menuitem"}
      aria-checked={typeof checked === "boolean" ? checked : undefined}
      onClick={props.onClick}
    >
      {content}
    </button>
  );
}
